import { Button } from '@client/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@client/components/ui/alert-dialog';
import { ChevronLeft, ChevronRight, SkipForward } from 'lucide-react';

interface AdvisorOnboardingFooterProps {
  title?: string;
  description?: string;
  currentStep: number;
  totalSteps: number;
  onNext?: () => void;
  onSkip?: () => void;
  onPrev?: () => void;
  isNextDisabled?: boolean;
  hideSkip?: boolean;
  lastStepButtonLabel?: string;
}

export function AdvisorOnboardingFooter({
  title,
  description,
  currentStep,
  totalSteps,
  onNext,
  onSkip,
  onPrev,
  isNextDisabled,
  hideSkip,
  lastStepButtonLabel = 'Go to Dashboard',
}: AdvisorOnboardingFooterProps) {
  const isPrevButtonVisible = currentStep > 0;
  const lastStep = currentStep + 1 === totalSteps;

  return (
    <div className="sticky bottom-0 border-t border-gray-200 bg-white">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-6 p-6">
        {!lastStep && !hideSkip && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                className="rounded-full p-6 text-base text-gray-600"
              >
                <SkipForward className="size-5" />
                Skip for now
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{title}</AlertDialogTitle>
                <AlertDialogDescription>{description}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="mt-4">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onSkip}>Skip Anyway</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        <div className="ml-auto flex items-center gap-4">
          {isPrevButtonVisible && (
            <Button
              size="lg"
              variant="outline"
              onClick={onPrev}
              className="rounded-full p-6 text-base"
            >
              <ChevronLeft className="size-5" />
              Back
            </Button>
          )}
          <Button
            size="lg"
            onClick={onNext}
            disabled={isNextDisabled}
            className="rounded-full p-6 text-base"
          >
            {lastStep ? lastStepButtonLabel : 'Next'}
            <ChevronRight className="size-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
